// Cartão "Áreas e CNAE" da Visão geral do contrato (04/10/2026).
// Pedido do Lucas: "na página do contrato precisa constar o quanto está
// associado a cada área". O quanto sai das PARCELAS (valor × setor) — a mesma
// conta da receita por setor da DRE; a divisão gravada no contrato é o padrão
// das parcelas novas. Num contrato principal, as parcelas dos aditivos entram.
import { useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Spin, Tag, Tooltip, Typography, App as AntApp } from 'antd';
import { Link } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import type { Contrato } from './types';
import { fmt, fmtData } from './utils';
import { SETORES } from '../../utils/areasEmpresa';
import { DetalheAreas, PENDENCIAS, CONFIANCA, rotuloOrigem, descreverRateio } from './areasContrato';
import { carregarAreasDoContrato, mensagemDeErro } from './apiAreas';
import { ModalAreasContrato } from './ModalAreasContrato';
import {
  avaliarContratoSetores, aceitarSetorCusto, desfazerAceiteSetorCusto,
  AvaliacaoContratoSetores, ItemAvaliacaoSetor,
} from '../administrativo-plano-acao/paginas/apiDre';
import { rotuloSetores, nomesSetores } from '../administrativo-plano-acao/paginas/dadosSetoresCusto';

const pct = (p: number) => (Math.round(p * 1000) / 10).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';

const ORIGEM_VALE: Record<string, string> = {
  parcelas: 'pelas parcelas', faturamento_antigo: 'pelo faturamento antigo',
  padrao_contrato: 'pelo padrão do contrato', rotulo_contrato: 'pelo rótulo do contrato', sem_setor: 'sem divisão',
};
const TIPO_ITEM: Record<string, string> = { pessoa: 'pessoa', lancamento: 'lançamento', bem: 'equipamento', nota: 'nota', outro: 'outro' };
const mesAno = (m: string) => { const [a, mm] = String(m).split('/'); return mm ? `${mm}/${a}` : m; };

// Bloco "Na Margem por setor": avalia ESTE contrato (divisão que vale, custo direto
// por setor e o que não casa) e deixa aceitar "segue o contrato" sem sair da página.
function BlocoMargemSetor({ contrato, av, erro, onRecarregar }: {
  contrato: Contrato; av: AvaliacaoContratoSetores | null;
  erro: { msg: string; semAcesso: boolean } | null; onRecarregar: () => void;
}) {
  const { message } = AntApp.useApp();
  const [ocupado, setOcupado] = useState(false);
  const peq = { fontSize: 12 } as const;
  if (erro?.semAcesso) return null;
  if (erro && !av) {
    return (
      <div style={{ ...peq, marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0', color: '#888' }}>
        Não consegui carregar a avaliação por setor.{' '}
        <Button size="small" type="link" style={{ padding: 0, ...peq }} onClick={onRecarregar}>Tentar de novo</Button>
      </div>
    );
  }
  if (!av) return null;

  async function agir(fn: () => Promise<any>, ok: string) {
    setOcupado(true);
    try {
      await fn();
      message.success(`${ok} Recalcule os meses do contrato na Margem por setor para a lista atualizar.`);
      onRecarregar();
    } catch (e: any) {
      message.error('Não consegui: ' + (e?.message || mensagemDeErro(e)));
    } finally { setOcupado(false); }
  }

  const porSetor = Object.entries(av.custos.por_setor || {})
    .map(([k, v]) => ({ k, v: Math.abs(v) }))
    .sort((a, b) => b.v - a.v);
  const totalAbs = Math.abs(av.custos.total || 0);
  const pend: ItemAvaliacaoSetor[] = [...av.pendencias.custos_sem_casamento, ...av.pendencias.custos_sem_setor];
  const temAceiteContrato = av.aceites.some(a => a.escopo === 'contrato');
  const mixTxt = Object.keys(av.vale.mix || {}).length ? rotuloSetores(av.vale.mix) : '—';

  return (
    <div style={{ ...peq, marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0', lineHeight: 1.7 }}>
      <Typography.Text strong style={peq}>Na Margem por setor</Typography.Text>
      <div>
        <Typography.Text type="secondary" style={peq}>Divisão que vale hoje: </Typography.Text>
        <b>{mixTxt}</b> — {ORIGEM_VALE[av.vale.origem] || av.vale.origem}
        {av.vale.origem !== 'parcelas' && (
          <div style={{ fontSize: 11, color: '#888' }}>
            O padrão do contrato só manda enquanto nenhuma parcela tem setor; com setor nas parcelas, vale o que elas dizem.
          </div>
        )}
      </div>

      <div style={{ marginTop: 4 }}>
        <Typography.Text type="secondary" style={peq}>Custo direto {mesAno(av.de)}–{mesAno(av.ate)}: </Typography.Text>
        <b>{fmt(totalAbs)}</b>
      </div>
      {av.custos.congelados_fonte === 'ficha_hoje' && av.custos.meses_congelados?.length ? (
        <div><Typography.Text type="secondary" style={peq}>
          {av.custos.meses_congelados.length === 1 ? 'Mês congelado' : `${av.custos.meses_congelados.length} meses congelados`} (antes do corte da análise): setor pela ficha de hoje, igual à Margem por setor.
        </Typography.Text></div>
      ) : null}
      {porSetor.map(({ k, v }) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '1px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: SETORES[k]?.cor || '#888', flexShrink: 0 }} />
          <span style={{ flex: 1, color: '#333' }}>{SETORES[k]?.rotulo || k}</span>
          <span style={{ color: '#666', minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</span>
          <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'right' }}>{totalAbs > 0 ? pct(v / totalAbs) : '—'}</span>
        </div>
      ))}

      {pend.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Typography.Text strong style={{ ...peq, color: '#B91626' }}>Custos que não casam com a divisão</Typography.Text>
          {pend.slice(0, 6).map((it, i) => {
            const aceitavel = it.tipo === 'pessoa' || it.tipo === 'bem';
            return (
              <div key={`${it.tipo}:${it.ref}:${i}`} style={{ padding: '2px 0', borderBottom: '1px dashed #f0f0f0' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                  <Tag style={{ margin: 0, fontSize: 10 }}>{TIPO_ITEM[it.tipo] || it.tipo}</Tag>
                  <b>{it.nome}</b>
                  {it.linha && <span style={{ color: '#888' }}>{it.linha}</span>}
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{fmt(Math.abs(it.valor))}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, color: '#666', fontSize: 11 }}>
                  <span>setor: {it.setores_item ? nomesSetores(it.setores_item) : 'sem setor'} · contrato vende: {it.mix ? nomesSetores(it.mix) : '—'}</span>
                  {it.aceito
                    ? <Tag color="green" style={{ margin: 0, fontSize: 10 }}>aceito — recalcule</Tag>
                    : aceitavel
                      ? (av.pode_aceitar && (
                        <Popconfirm title={`Aceitar que ${it.nome} siga a divisão do contrato ${it.numero || contrato.numero}?`}
                          okText="Aceitar" cancelText="Cancelar"
                          onConfirm={() => agir(() => aceitarSetorCusto({ contrato_id: it.contrato_id, escopo: it.tipo as 'pessoa' | 'bem', ref: it.ref }), 'Aceite registrado.')}>
                          <Button size="small" disabled={ocupado} style={{ fontSize: 11, height: 22 }}>Aceitar: segue o contrato</Button>
                        </Popconfirm>
                      ))
                      : <Link to="/financeiro/dre-hub?aba=setores-custo">revise na aba Setor dos custos</Link>}
                </div>
              </div>
            );
          })}
          {pend.length > 6 && <div style={{ color: '#888', fontSize: 11 }}>e mais {pend.length - 6}</div>}
        </div>
      )}

      {av.pode_aceitar && !temAceiteContrato && (
        <div style={{ marginTop: 6 }}>
          <Popconfirm
            title={`No ${contrato.numero}, todo custo cujo setor o contrato não vende passa a seguir a divisão do contrato? O que já casa não muda; marcação manual vence.`}
            okText="Sim, seguir o contrato" cancelText="Cancelar"
            onConfirm={() => agir(() => aceitarSetorCusto({ contrato_id: av.contrato.raiz, escopo: 'contrato', ref: null }), 'Aceite registrado.')}>
            <Button size="small" disabled={ocupado}>Custos que não casam seguem o contrato</Button>
          </Popconfirm>
        </div>
      )}

      {av.aceites.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Typography.Text type="secondary" style={peq}>Aceites registrados</Typography.Text>
          {av.aceites.map(a => (
            <div key={a.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
              <span>{a.escopo === 'contrato' ? 'todo o contrato' : `${a.escopo === 'pessoa' ? 'pessoa' : 'equipamento'}: ${a.nome}`}</span>
              <span style={{ color: '#888' }}>— {a.por}, {fmtData(a.em)}</span>
              {av.pode_aceitar && (
                <Popconfirm title="Desfazer este aceite?" okText="Desfazer" cancelText="Manter"
                  onConfirm={() => agir(() => desfazerAceiteSetorCusto(a.id), 'Aceite desfeito.')}>
                  <Button size="small" type="link" danger disabled={ocupado} style={{ padding: 0, height: 20, fontSize: 11 }}>Desfazer</Button>
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}

      {av.parcelas.sem_setor > 0 && (
        <div style={{ color: '#B91626', marginTop: 4 }}>
          {av.parcelas.sem_setor} parcela{av.parcelas.sem_setor === 1 ? '' : 's'} sem setor ({fmt(av.parcelas.valor_sem_setor)}) — use Editar › O que fazer com as parcelas.
        </div>
      )}
    </div>
  );
}

export function CardAreasCnae({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const [det, setDet] = useState<DetalheAreas | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<{ msg: string; semAcesso: boolean } | null>(null);
  const [editando, setEditando] = useState(false);
  const [av, setAv] = useState<AvaliacaoContratoSetores | null>(null);
  const [erroAv, setErroAv] = useState<{ msg: string; semAcesso: boolean } | null>(null);

  async function carregarAv() {
    setErroAv(null);
    try {
      setAv(await avaliarContratoSetores(contrato.id));
    } catch (e: any) {
      setAv(null);
      setErroAv({ msg: mensagemDeErro(e), semAcesso: e?.status === 403 || e?.response?.status === 403 });
    }
  }

  async function carregar() {
    setCarregando(true); setErro(null);
    try {
      setDet(await carregarAreasDoContrato(contrato.id));
    } catch (e: any) {
      // Falha de leitura NÃO vira "sem áreas": fica o erro, com o botão de tentar.
      setDet(null);
      setErro({ msg: mensagemDeErro(e), semAcesso: e?.response?.status === 403 });
    } finally { setCarregando(false); }
  }
  useEffect(() => {
    carregar();
    carregarAv();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato.id]);

  if (carregando && !det) return <div style={{ textAlign: 'center', padding: 18 }}><Spin size="small" /></div>;
  if (erro && !det) {
    return erro.semAcesso
      ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>Seu perfil não vê as áreas e o CNAE do contrato.</Typography.Text>
      : <Alert type="error" showIcon message="Não consegui carregar as áreas" description={erro.msg}
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />;
  }
  if (!det) return null;

  const temParcelas = det.numeros.parcelas > 0;
  // A barra e a lista são SÓ das parcelas. A divisão gravada no contrato (o
  // padrão das parcelas novas) aparece uma vez, na linha "Parcela nova nasce
  // com" — usá-la também na barra fazia um contrato com parcelas todas sem
  // setor mostrar "Topografia 100%" e "Sem setor 100%" juntos.
  const linhas = det.por_area.map(a => ({ chave: a.setor, rotulo: a.rotulo, cor: SETORES[a.setor]?.cor || '#888', proporcao: a.proporcao, valor: a.valor }));
  const semSetor = det.valores?.sem_setor || 0;
  const propSemSetor = det.valores && det.valores.total > 0 ? semSetor / det.valores.total : 0;
  const sugD = det.sugestao.divisao, sugC = det.sugestao.cnae;
  const etiquetas = [...det.pendencias, ...det.avisos].filter(p =>
    p === 'parcelas_sem_cnae' || p === 'cnae_divergente' || p === 'setor_divergente' || (p === 'parcelas_sem_setor' && !(semSetor > 0)));

  return (
    <div>
      {(linhas.length > 0 || propSemSetor > 0) && (
        <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eee', marginBottom: 10 }}>
          {linhas.map(l => <div key={l.chave} title={`${l.rotulo}: ${pct(l.proporcao)}`} style={{ background: l.cor, width: `${l.proporcao * 100}%` }} />)}
          {propSemSetor > 0 && <div title={`Sem setor: ${pct(propSemSetor)}`} style={{ background: '#d9d9d9', width: `${propSemSetor * 100}%` }} />}
        </div>
      )}
      {linhas.map(l => (
        <div key={l.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '2px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: l.cor, flexShrink: 0 }} />
          <span style={{ flex: 1, color: '#333' }}>{l.rotulo}</span>
          <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'right' }}>{pct(l.proporcao)}</span>
          {l.valor != null && (
            <span style={{ color: '#666', minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.valor)}</span>
          )}
        </div>
      ))}
      {semSetor > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '2px 0', color: '#B91626' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#d9d9d9', flexShrink: 0 }} />
          <span style={{ flex: 1, fontWeight: 600 }}>Sem setor ({det.numeros.sem_setor_n} parcela{det.numeros.sem_setor_n === 1 ? '' : 's'})</span>
          <span style={{ fontWeight: 600, minWidth: 48, textAlign: 'right' }}>{pct(propSemSetor)}</span>
          <span style={{ minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(semSetor)}</span>
        </div>
      )}
      {!linhas.length && !semSetor && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {temParcelas ? 'As parcelas ainda não têm setor.' : 'Ainda não há parcelas neste contrato.'}
        </Typography.Text>
      )}
      {(det.por_area.length > 0 || semSetor > 0) && (
        <div style={{ fontSize: 10.5, color: '#999', marginTop: 4 }}>
          Pelas parcelas do contrato{det.contrato.aditivos ? ` e dos ${det.contrato.aditivos} aditivo(s)` : ''} — a mesma conta da receita por setor da DRE.
        </div>
      )}

      {det.subareas.some(s => s.subarea) && (
        <div style={{ fontSize: 11.5, color: '#555', marginTop: 8 }}>
          <b>Sub-áreas (pela venda):</b>{' '}
          {det.subareas.filter(s => s.subarea).map(s => `${s.rotulo} › ${s.subarea}${s.valor != null ? ` ${fmt(s.valor)}` : ''}`).join(' · ')}
        </div>
      )}

      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0', fontSize: 12, lineHeight: 1.7 }}>
        <div>
          <Typography.Text type="secondary">Parcela nova nasce com: </Typography.Text>
          {det.divisao.rateio
            ? <><b>{descreverRateio(det.divisao.rateio)}</b>{' '}
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ({det.divisao.herdada ? `segue o contrato principal ${det.contrato.pai_numero || ''}` : `origem: ${rotuloOrigem(det.divisao.origem)}`})
                </Typography.Text></>
            : <Tag color="red">divisão por área não definida</Tag>}
        </div>
        <div>
          <Typography.Text type="secondary">CNAE do contrato: </Typography.Text>
          {det.cnae.codigo
            ? <><b>{det.cnae.codigo}</b>{det.cnae.descricao ? ` — ${det.cnae.descricao}` : ''}{' '}
                {det.cnae.herdado && <Tag style={{ fontSize: 10 }}>do contrato principal</Tag>}
                {det.cnae.manual && <Tag style={{ fontSize: 10 }}>escolhido à mão</Tag>}</>
            : <Tag color="orange">não definido</Tag>}
        </div>
        {!det.divisao.rateio && sugD && (
          <div style={{ color: '#666' }}>
            Sugestão de divisão <Tag color={CONFIANCA[sugD.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugD.confianca].rotulo}</Tag>
            {descreverRateio(sugD.rateio)} ({rotuloOrigem(sugD.origem)})
          </div>
        )}
        {!det.cnae.codigo && sugC && (
          <div style={{ color: '#666' }}>
            Sugestão de CNAE <Tag color={CONFIANCA[sugC.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugC.confianca].rotulo}</Tag>
            {sugC.codigo} ({rotuloOrigem(sugC.origem)})
          </div>
        )}
      </div>

      <BlocoMargemSetor contrato={contrato} av={av} erro={erroAv} onRecarregar={carregarAv} />

      {/* Etiqueta só para o que o cartão ainda não disse: "sem divisão" e "sem
          CNAE" já estão nas duas linhas acima, e a parcela sem setor já tem a
          linha vermelha com o valor (quando o perfil vê valores). */}
      {etiquetas.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {etiquetas.map(p => {
            const cfg = PENDENCIAS[p];
            const quantos = p === 'parcelas_sem_cnae' ? det.numeros.abertas_sem_cnae_n
              : p === 'cnae_divergente' ? det.numeros.cnae_divergente_n
              : p === 'parcelas_sem_setor' ? det.numeros.sem_setor_n : 0;
            return (
              <Tooltip key={p} title={cfg?.dica}>
                <Tag color={cfg?.cor || 'default'} style={{ margin: 0 }}>{cfg?.rotulo || p}{quantos ? ` (${quantos})` : ''}</Tag>
              </Tooltip>
            );
          })}
        </div>
      )}

      {det.pode_editar && (
        <div style={{ marginTop: 10 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(true)}>
            {det.pendencias.length ? 'Arrumar áreas e CNAE' : 'Editar'}
          </Button>
        </div>
      )}

      <ModalAreasContrato
        contratoId={contrato.id} aberto={editando}
        onFechar={() => setEditando(false)}
        podeAceitar={av?.pode_aceitar}
        aceiteContratoExiste={av?.aceites.some(a => a.escopo === 'contrato')}
        onSalvo={(d) => { setEditando(false); setDet(d); onAtualizar(); carregarAv(); }} />
    </div>
  );
}
